"use client"

import type React from "react"

import { usePathname } from "next/navigation"
import Script from "next/script"
import { useEffect, useState } from "react"
import Image from "next/image"
import { LINKEDIN_PARTNER_ID, META_PIXEL_ID, trackPageView } from "@/lib/analytics"

// Analytics configuration
const GOOGLE_ANALYTICS_ID = "G-0C6S0G00L4"
// const GOOGLE_ADS_ID = "AW-11280033962"
const HYROS_TRACKING_ID = "a14706c5509cc31c309903102a299cc280765fdf7ce913f7879a18fa9b85cf1e"
const AHREFS_KEY = "/VtEhj/RPFBzbqqfZW9OAw"
const HUBSPOT_ID = "40059086"
const TOLT_ID = "bd74307a-cc8a-4334-bbcb-ab0a289a7b42"

export default function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const [searchParams, setSearchParams] = useState<URLSearchParams | null>(null)

  // Safely get search params on the client side only
  useEffect(() => {
    if (typeof window !== "undefined") {
      setSearchParams(new URLSearchParams(window.location.search))
    }
  }, [])

  // Track page views when route changes
  useEffect(() => {
    if (pathname) {
      trackPageView(pathname, document.title)
    }
  }, [pathname, searchParams])

  return (
    <>
      {children}

      {/* Google Analytics - Critical */}
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`}
        strategy="afterInteractive"
        onLoad={() => {
          window.dataLayer = window.dataLayer || []
          function gtag(...args: unknown[]) {
            window.dataLayer.push(args)
          }
          window.gtag = gtag
          gtag("js", new Date())
          gtag("set", "developer_id.dZGVlNj", true)
          gtag("config", GOOGLE_ANALYTICS_ID)
        }}
      />

      {/* Google Ads - Critical */}
      {/* FIXME: temp commented out, probably wrong configuration */}
      {/* <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`}
        strategy="afterInteractive"
        onLoad={() => {
          window.gtag("config", GOOGLE_ADS_ID)
        }}
      /> */}

      {/* Facebook Pixel - Critical */}
      <Script id="meta-pixel-init" strategy="afterInteractive">
        {`
         !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
         n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
         n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
         t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
         document,'script','https://connect.facebook.net/en_US/fbevents.js');
         fbq('init', '${META_PIXEL_ID}');
         fbq('track', 'PageView');
       `}
      </Script>

      {/* Custom 5x5 Tracking Script - Load after interactive */}
      <Script
        id="usbrowserspeed-script"
        strategy="afterInteractive"
        onLoad={() => {
          const customScript = document.createElement('script');
          customScript.type = 'text/javascript';
          customScript.src = "https://a.usbrowserspeed.com/cs?pid=aa751aed52f91785534b0093667c03d1520ccc2ac83da3f311fc1b165845bfc8&puid=j9o6749k1aw5qp00hyr9yh2zzx0nfdjxndhf1ko7c8p4sgz6k5tuweqrpctuu395";
          document.body.appendChild(customScript);
        }}
        />
        
      <noscript>
        <Image
          src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`}
          alt="Meta pixel"
          width={1}
          height={1}
          style={{ display: "none" }}
        />
      </noscript>

      {/* LinkedIn Tracking */}
      <Script id="linkedin-tracking" strategy="afterInteractive">
        {`
          _linkedin_partner_id = "${LINKEDIN_PARTNER_ID}";
          window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
          window._linkedin_data_partner_ids.push(_linkedin_partner_id);
          
          (function() {
            var s = document.getElementsByTagName("script")[0];
            var b = document.createElement("script");
            b.type = "text/javascript";
            b.async = true;
            b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
            s.parentNode.insertBefore(b, s);
          })();
        `}
      </Script>
      <noscript>
        <Image
          src={`https://px.ads.linkedin.com/collect/?pid=${LINKEDIN_PARTNER_ID}&fmt=gif`}
          alt="LinkedIn tracking pixel"
          width={1}
          height={1}
          style={{ display: "none" }}
        />
      </noscript>

      {/* HubSpot - Load with high priority but after interactive */}
      <Script
        id="hubspot-script"
        src={`https://js.hs-scripts.com/${HUBSPOT_ID}.js`}
        strategy="afterInteractive"
        onLoad={() => {
          window._hsq = window._hsq || []
          window._hsq.push(["setPath", pathname])
          window._hsq.push(["trackPageView"])
        }}
      />

      {/* Ahrefs Analytics - Load after interactive */}
      <Script src="https://analytics.ahrefs.com/analytics.js" data-key={AHREFS_KEY} strategy="afterInteractive" />

      {/* Tolt Referral Script - Load after interactive */}
      <Script id="tolt-script" src="https://cdn.tolt.io/tolt.js" data-tolt={TOLT_ID} strategy="afterInteractive" />

      {/* Hyros Tracking - Load with lower priority */}
      {typeof window !== "undefined" && (
        <Script
          id="hyros-tracking"
          strategy="lazyOnload"
          src={`https://184350.t.hyros.com/v1/lst/universal-script?ph=${HYROS_TRACKING_ID}&tag=!clicked&ref_url=${encodeURIComponent(window.location.href)}`}
        />
      )}

    </>
  )
}

